import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { courseV2Api } from '../services/api';
import { CourseV2 } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { 
  ChevronRightIcon, 
  ClockIcon, 
  UsersIcon, 
  CheckCircleIcon, 
  AcademicCapIcon,
  BookOpenIcon,
  StarIcon,
  PlayIcon
} from '../components/icons';

const CourseV2DetailPage: React.FC = () => {
  const { courseId = '' } = useParams<{ courseId: string }>();
  const { colors } = useTheme();
  const [course, setCourse] = useState<CourseV2 | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'syllabus'>('overview');

  useEffect(() => {
    fetchCourse();
  }, [courseId]);

  const fetchCourse = async () => {
    if (!courseId) {
      setError('Course ID is required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await courseV2Api.getById(courseId);
      setCourse(response.data.data.course);
      setError(null);
    } catch (err) {
      setError('Failed to load course details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen" style={{ backgroundColor: colors.background.default }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: colors.accent.mint }}></div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="container mx-auto px-4 py-8 text-center" style={{ backgroundColor: colors.background.default }}>
        <p className="text-lg mb-4" style={{ color: colors.error.main }}>{error || 'Course not found'}</p>
        <Link
          to="/coursesv2"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105"
          style={{
            backgroundColor: `${colors.accent.mint}20`,
            color: colors.accent.mint
          }}
        >
          <ChevronRightIcon className="w-5 h-5 rotate-180" />
          Back to Courses
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16" style={{ backgroundColor: colors.background.default }}>
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="relative h-[500px]">
          <img 
            src={course.image || `https://picsum.photos/seed/${course._id}/1200/500`} 
            alt={course.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src = `https://picsum.photos/seed/${course._id}/1200/500`;
            }}
          />
          <div 
            className="absolute inset-0"
            style={{
              background: `linear-gradient(to bottom, ${colors.background.default}00 0%, ${colors.background.default}ff 100%)`
            }}
          />
          <div 
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, ${colors.primary.main}80 0%, ${colors.accent.mint}80 100%)`
            }}
          />
          
          <div className="absolute bottom-0 left-0 right-0 p-8">
            <div className="container mx-auto">
              <div className="flex items-center gap-4 mb-4">
                <span 
                  className="px-4 py-2 rounded-full text-sm font-semibold"
                  style={{
                    backgroundColor: colors.accent.warmYellow,
                    color: colors.primary.contrastText
                  }}
                >
                  {course.examType}
                </span>
                <span 
                  className="px-4 py-2 rounded-full text-sm font-semibold"
                  style={{
                    backgroundColor: `${colors.background.paper}90`,
                    color: colors.text.primary
                  }}
                >
                  {course.mode}
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                {course.title}
              </h1>
              
              <p className="text-lg text-white/90 mb-6 max-w-3xl">
                {course.description}
              </p>
              
              <div className="flex flex-wrap gap-3 mb-8">
                {course.subjects.map((subject, index) => (
                  <span 
                    key={index}
                    className="px-4 py-2 rounded-full font-medium bg-white/20 text-white backdrop-blur-sm"
                  >
                    {subject}
                  </span>
                ))}
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-lg"
                  style={{
                    background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`,
                    color: colors.primary.contrastText
                  }}
                >
                  Book a Demo Class
                  <ChevronRightIcon className="w-5 h-5" />
                </Link>
                <button
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-lg font-semibold text-lg border-2 transition-all duration-300 hover:scale-105 bg-white/10 backdrop-blur-sm"
                  style={{
                    borderColor: 'white',
                    color: 'white'
                  }}
                >
                  <PlayIcon className="w-5 h-5" />
                  Watch Video
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Course Information */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* Tabs */}
              <div 
                className="flex gap-4 mb-8 p-1 rounded-lg"
                style={{ backgroundColor: colors.background.paper }}
              >
                {(['overview', 'syllabus'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className="flex-1 px-6 py-3 rounded-lg font-medium capitalize transition-all duration-300"
                    style={{
                      backgroundColor: activeTab === tab ? colors.primary.main : 'transparent',
                      color: activeTab === tab ? colors.primary.contrastText : colors.text.secondary
                    }}
                  >
                    {tab}
                  </button>
                ))}
              </div>

              {/* Tab Content */}
              <div 
                className="rounded-xl p-8"
                style={{ backgroundColor: colors.background.paper }}
              >
                {activeTab === 'overview' && (
                  <div>
                    <h2 className="text-2xl font-bold mb-6" style={{ color: colors.text.primary }}>
                      Course Overview
                    </h2>
                    
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: colors.text.primary }}>
                          <CheckCircleIcon className="w-5 h-5" style={{ color: colors.accent.mint }} />
                          Key Features
                        </h3>
                        <ul className="space-y-2">
                          {course.features.map((feature, index) => (
                            <li key={index} className="flex items-start gap-3">
                              <CheckCircleIcon className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: colors.accent.mint }} />
                              <span style={{ color: colors.text.secondary }}>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {course.highlights.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: colors.text.primary }}>
                            <StarIcon className="w-5 h-5" style={{ color: colors.accent.warmYellow }} />
                            Course Highlights
                          </h3>
                          <ul className="space-y-2">
                            {course.highlights.map((highlight, index) => (
                              <li key={index} className="flex items-start gap-3">
                                <span style={{ color: colors.accent.warmYellow }}>★</span>
                                <span style={{ color: colors.text.secondary }}>{highlight}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div>
                        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ color: colors.text.primary }}>
                          <ClockIcon className="w-5 h-5" style={{ color: colors.accent.mint }} />
                          Duration & Schedule
                        </h3>
                        <p style={{ color: colors.text.secondary }}>{course.duration}</p>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'syllabus' && (
                  <div>
                    <h2 className="text-2xl font-bold mb-6" style={{ color: colors.text.primary }}>
                      Course Syllabus
                    </h2>
                    
                    <div className="space-y-3">
                      {course.syllabusSummary.map((topic, index) => (
                        <div 
                          key={index}
                          className="flex items-center gap-4 p-4 rounded-lg transition-all duration-300 hover:scale-[1.02]"
                          style={{ 
                            backgroundColor: colors.background.default,
                            borderLeft: `4px solid ${colors.accent.mint}`
                          }}
                        >
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm"
                            style={{
                              backgroundColor: `${colors.accent.mint}20`,
                              color: colors.accent.mint
                            }}
                          >
                            {index + 1}
                          </div>
                          <span style={{ color: colors.text.primary }}>{topic}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div 
                className="sticky top-24 rounded-xl p-6"
                style={{ 
                  backgroundColor: colors.background.paper,
                  boxShadow: `0 10px 30px ${colors.primary.main}10`
                }}
              >
                {course.price && (course.price.hourly || course.price.package) && (
                  <>
                    <h3 className="text-xl font-bold mb-6" style={{ color: colors.text.primary }}>
                      Pricing Options
                    </h3>
                    
                    <div className="space-y-4 mb-6">
                      {course.price.hourly && (
                        <div 
                          className="p-4 rounded-lg border-2"
                          style={{ 
                            borderColor: colors.accent.mint,
                            backgroundColor: `${colors.accent.mint}10`
                          }}
                        >
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium" style={{ color: colors.text.primary }}>Hourly Rate</span>
                            <span className="text-2xl font-bold" style={{ color: colors.accent.mint }}>
                              ${course.price.hourly}
                            </span>
                          </div>
                          <p className="text-sm" style={{ color: colors.text.secondary }}>
                            Pay as you go
                          </p>
                        </div>
                      )}
                      
                      {course.price.package && (
                        <div 
                          className="p-4 rounded-lg"
                          style={{ 
                            backgroundColor: colors.background.default
                          }}
                        >
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium" style={{ color: colors.text.primary }}>Monthly Package</span>
                            <span className="text-2xl font-bold" style={{ color: colors.primary.main }}>
                              ${course.price.package}
                            </span>
                          </div>
                          <p className="text-sm" style={{ color: colors.text.secondary }}>
                            Best value for regular sessions
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                )}
                
                <Link
                  to="/contact"
                  className="block w-full text-center px-6 py-3 rounded-lg font-semibold transition-all duration-300 hover:scale-105"
                  style={{
                    background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`,
                    color: colors.primary.contrastText
                  }}
                >
                  Enroll Now
                </Link>
                
                <div className="mt-6 space-y-3">
                  {[
                    { icon: CheckCircleIcon, text: "Personalized study plan" },
                    { icon: ClockIcon, text: "Flexible scheduling" },
                    { icon: AcademicCapIcon, text: "Expert guidance" },
                    { icon: BookOpenIcon, text: "Comprehensive materials" }
                  ].map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <item.icon className="w-5 h-5" style={{ color: colors.accent.mint }} />
                      <span className="text-sm" style={{ color: colors.text.secondary }}>
                        {item.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CourseV2DetailPage;